import React, { useState } from 'react';
import styled from 'styled-components';
import { FileSpreadsheet, Filter, Search, Eye, MessageSquare, Calendar, Clock, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';
import ContentLayout from '../../../Common/Component/ContentLayout';

const Container = styled.div`
  width: 100%;
`;

const FilterSection = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin-bottom: 20px;
  display: flex;
  gap: 15px;
  align-items: center;
  flex-wrap: wrap;
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  min-width: 120px;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const SearchInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  min-width: 200px;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const QuotesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const QuoteCard = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  overflow: hidden;
  transition: all 0.2s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  }
`;

const QuoteHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid #f3f4f6;
`;

const QuoteTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 15px;
`;

const QuoteInfo = styled.div`
  flex: 1;
`;

const QuoteId = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 5px 0;
`;

const QuoteTitle = styled.p`
  font-size: 14px;
  color: #374151;
  margin: 0 0 5px 0;
`;

const QuoteDate = styled.p`
  font-size: 12px;
  color: #6b7280;
  margin: 0;
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 15px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  background: ${props => {
    switch(props.status) {
      case 'pending': return '#fef3c7';
      case 'in-progress': return '#dbeafe';
      case 'quoted': return '#e0f2fe';
      case 'completed': return '#dcfce7';
      case 'cancelled': return '#fee2e2';
      default: return '#f3f4f6';
    }
  }};
  color: ${props => {
    switch(props.status) {
      case 'pending': return '#d97706';
      case 'in-progress': return '#2563eb';
      case 'quoted': return '#0369a1';
      case 'completed': return '#16a34a';
      case 'cancelled': return '#dc2626';
      default: return '#6b7280';
    }
  }};
`;

const QuoteDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 15px;
`;

const DetailItem = styled.div``;

const DetailLabel = styled.p`
  font-size: 11px;
  color: #6b7280;
  font-weight: 600;
  text-transform: uppercase;
  margin: 0 0 3px 0;
`;

const DetailValue = styled.p`
  font-size: 13px;
  color: #1f2937;
  margin: 0;
  font-weight: 500;
`;

const QuoteActions = styled.div`
  padding: 15px 20px;
  background: #f9fafb;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  background: ${props => props.primary ? '#3b82f6' : 'transparent'};
  color: ${props => props.primary ? 'white' : '#374151'};
  border: ${props => props.primary ? 'none' : '1px solid #d1d5db'};
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.primary ? '#2563eb' : '#f3f4f6'};
  }
`;

const QuoteAmount = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #3b82f6;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
`;

const EmptyIcon = styled.div`
  width: 80px;
  height: 80px;
  background: #f3f4f6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: #9ca3af;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: #e5e7eb;
  border-radius: 3px;
  overflow: hidden;
  margin: 10px 0;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: ${props => {
    switch(props.status) {
      case 'pending': return '#fbbf24';
      case 'in-progress': return '#3b82f6';
      case 'quoted': return '#0ea5e9';
      case 'completed': return '#10b981';
      case 'cancelled': return '#ef4444';
      default: return '#9ca3af';
    }
  }};
  width: ${props => {
    switch(props.status) {
      case 'pending': return '25%';
      case 'in-progress': return '50%';
      case 'quoted': return '75%';
      case 'completed': return '100%';
      case 'cancelled': return '0%';
      default: return '0%';
    }
  }};
  transition: width 0.3s ease;
`;

function MyQuotes() {
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // 더미 견적 데이터
  const quotesData = [
    {
      id: 'QT-2024-001',
      title: '홈페이지 리뉴얼 견적 요청',
      category: '웹개발',
      requestDate: '2024-02-15',
      status: 'in-progress',
      budget: '300-500',
      deadline: '2024-03-15',
      description: '기존 홈페이지를 반응형으로 리뉴얼하고 싶습니다.',
      estimatedAmount: null,
      lastUpdate: '2024-02-16',
      progress: 50
    },
    {
      id: 'QT-2024-002',
      title: '모바일 앱 개발',
      category: '앱개발',
      requestDate: '2024-02-10',
      status: 'quoted',
      budget: '500-1000',
      deadline: '2024-04-30',
      description: 'iOS/Android 쇼핑몰 앱 개발',
      estimatedAmount: 850,
      lastUpdate: '2024-02-14',
      progress: 75
    },
    {
      id: 'QT-2024-003',
      title: '로고 디자인',
      category: '디자인',
      requestDate: '2024-02-05',
      status: 'completed',
      budget: '100-300',
      deadline: '2024-02-20',
      description: '새로운 브랜드 로고 디자인',
      estimatedAmount: 180,
      lastUpdate: '2024-02-20',
      progress: 100
    },
    {
      id: 'QT-2024-004',
      title: 'SEO 컨설팅',
      category: '마케팅',
      requestDate: '2024-01-28',
      status: 'pending',
      budget: '100-300',
      deadline: '2024-03-01',
      description: '웹사이트 SEO 최적화 컨설팅',
      estimatedAmount: null,
      lastUpdate: '2024-01-28',
      progress: 25
    },
    {
      id: 'QT-2024-005',
      title: 'ERP 시스템 개발',
      category: '웹개발',
      requestDate: '2024-01-20',
      status: 'cancelled',
      budget: '1000+',
      deadline: '2024-06-30',
      description: '사내 ERP 시스템 구축',
      estimatedAmount: null,
      lastUpdate: '2024-01-25',
      progress: 0
    }
  ];

  const filteredQuotes = quotesData.filter(quote => {
    const matchesStatus = statusFilter === 'all' || quote.status === statusFilter;
    const matchesCategory = categoryFilter === 'all' || quote.category === categoryFilter;
    const matchesSearch = searchTerm === '' || 
      quote.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quote.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quote.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesStatus && matchesCategory && matchesSearch;
  });

  const getStatusIcon = (status) => {
    switch(status) {
      case 'pending': return <Clock size={14} />;
      case 'in-progress': return <RefreshCw size={14} />;
      case 'quoted': return <FileSpreadsheet size={14} />;
      case 'completed': return <CheckCircle size={14} />;
      case 'cancelled': return <AlertCircle size={14} />;
      default: return <Clock size={14} />;
    }
  };

  const getStatusText = (status) => {
    switch(status) {
      case 'pending': return '검토중';
      case 'in-progress': return '진행중';
      case 'quoted': return '견적완료';
      case 'completed': return '완료';
      case 'cancelled': return '취소';
      default: return '알수없음';
    }
  };

  const getBudgetText = (budget) => {
    switch(budget) {
      case 'under-100': return '100만원 미만';
      case '100-300': return '100-300만원';
      case '300-500': return '300-500만원';
      case '500-1000': return '500-1000만원';
      case 'over-1000': return '1000만원 이상';
      default: return budget;
    }
  };

  const getProgressText = (status) => {
    switch(status) {
      case 'pending': return '견적 검토중';
      case 'in-progress': return '요구사항 분석중';
      case 'quoted': return '견적서 전달완료';
      case 'completed': return '프로젝트 완료';
      case 'cancelled': return '견적 취소됨';
      default: return '';
    }
  };

  return (
    <ContentLayout 
      title="내 견적 목록"
      subtitle="요청하신 견적들의 진행 상황을 확인하고 관리할 수 있습니다"
    >
      <Container>
        <FilterSection>
          <FilterSelect 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">전체 상태</option>
            <option value="pending">검토중</option>
            <option value="in-progress">진행중</option>
            <option value="quoted">견적완료</option>
            <option value="completed">완료</option>
            <option value="cancelled">취소</option>
          </FilterSelect>

          <FilterSelect 
            value={categoryFilter} 
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="all">전체 카테고리</option>
            <option value="웹개발">웹개발</option>
            <option value="앱개발">앱개발</option>
            <option value="디자인">디자인</option>
            <option value="마케팅">마케팅</option>
            <option value="컨설팅">컨설팅</option>
          </FilterSelect>

          <SearchInput
            type="text"
            placeholder="견적번호, 제목, 내용 검색..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </FilterSection>

        {filteredQuotes.length > 0 ? (
          <QuotesList>
            {filteredQuotes.map(quote => (
              <QuoteCard key={quote.id}>
                <QuoteHeader>
                  <QuoteTop>
                    <QuoteInfo>
                      <QuoteId>{quote.id}</QuoteId>
                      <QuoteTitle>{quote.title}</QuoteTitle>
                      <QuoteDate>요청일: {quote.requestDate}</QuoteDate>
                    </QuoteInfo>
                    <StatusBadge status={quote.status}>
                      {getStatusIcon(quote.status)}
                      {getStatusText(quote.status)}
                    </StatusBadge>
                  </QuoteTop>
                  
                  <QuoteDetails>
                    <DetailItem>
                      <DetailLabel>카테고리</DetailLabel>
                      <DetailValue>{quote.category}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>예산범위</DetailLabel>
                      <DetailValue>{getBudgetText(quote.budget)}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>희망완료일</DetailLabel>
                      <DetailValue>{quote.deadline}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>마지막 업데이트</DetailLabel>
                      <DetailValue>{quote.lastUpdate}</DetailValue>
                    </DetailItem>
                  </QuoteDetails>
                  
                  <div style={{marginTop: '15px'}}>
                    <div style={{
                      fontSize: '12px',
                      color: '#6b7280',
                      marginBottom: '5px'
                    }}>
                      진행상황: {getProgressText(quote.status)}
                    </div>
                    <ProgressBar>
                      <ProgressFill status={quote.status} />
                    </ProgressBar>
                  </div>
                </QuoteHeader>

                <QuoteActions>
                  <div style={{flex: 1}}>
                    {quote.estimatedAmount ? (
                      <QuoteAmount>
                        견적금액: {quote.estimatedAmount.toLocaleString()}만원
                      </QuoteAmount>
                    ) : (
                      <div style={{fontSize: '14px', color: '#6b7280'}}>
                        견적 산정 중...
                      </div>
                    )}
                  </div>
                  
                  <div style={{display: 'flex', gap: '8px'}}>
                    <ActionButton primary>
                      <Eye size={12} />
                      상세보기
                    </ActionButton>
                    {quote.status === 'quoted' && (
                      <ActionButton>
                        <CheckCircle size={12} />
                        승인하기
                      </ActionButton>
                    )}
                    {quote.status !== 'completed' && quote.status !== 'cancelled' && (
                      <ActionButton>
                        <MessageSquare size={12} />
                        문의하기
                      </ActionButton>
                    )}
                  </div>
                </QuoteActions>
              </QuoteCard>
            ))}
          </QuotesList>
        ) : (
          <EmptyState>
            <EmptyIcon>
              <FileSpreadsheet size={40} />
            </EmptyIcon>
            <h3 style={{margin: '0 0 10px 0', color: '#374151'}}>
              견적 요청 내역이 없습니다
            </h3>
            <p style={{margin: 0, color: '#6b7280'}}>
              새로운 견적을 요청하거나 검색 조건을 변경해보세요
            </p>
          </EmptyState>
        )}
      </Container>
    </ContentLayout>
  );
}

export default MyQuotes;